import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { AcceptChangeIdentityEmailQueryResponse, AcceptChangeIdentityEmailPathParams } from "../types/AcceptChangeIdentityEmail";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type AcceptChangeIdentityEmailClient = typeof client<AcceptChangeIdentityEmailQueryResponse, never, never>;
type AcceptChangeIdentityEmail = {
    data: AcceptChangeIdentityEmailQueryResponse;
    error: never;
    request: never;
    pathParams: AcceptChangeIdentityEmailPathParams;
    queryParams: never;
    headerParams: never;
    response: AcceptChangeIdentityEmailQueryResponse;
    client: {
        parameters: Partial<Parameters<AcceptChangeIdentityEmailClient>[0]>;
        return: Awaited<ReturnType<AcceptChangeIdentityEmailClient>>;
    };
};
export const acceptChangeIdentityEmailQueryKey = (requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"]) => [{ url: "/api/identity/change-email/:requestedEmailChangeToken", params: { requestedEmailChangeToken: requestedEmailChangeToken } }] as const;
export type AcceptChangeIdentityEmailQueryKey = ReturnType<typeof acceptChangeIdentityEmailQueryKey>;
export function acceptChangeIdentityEmailQueryOptions(requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"], options: AcceptChangeIdentityEmail["client"]["parameters"] = {}) {
    const queryKey = acceptChangeIdentityEmailQueryKey(requestedEmailChangeToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<AcceptChangeIdentityEmail["data"], AcceptChangeIdentityEmail["error"]>({
                method: "get",
                url: `/api/identity/change-email/${requestedEmailChangeToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Change email confirmation
 * @link /api/identity/change-email/:requestedEmailChangeToken
 */
export function useAcceptChangeIdentityEmail<TData = AcceptChangeIdentityEmail["response"], TQueryData = AcceptChangeIdentityEmail["response"], TQueryKey extends QueryKey = AcceptChangeIdentityEmailQueryKey>(requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"], options: {
    query?: Partial<QueryObserverOptions<AcceptChangeIdentityEmail["response"], AcceptChangeIdentityEmail["error"], TData, TQueryData, TQueryKey>>;
    client?: AcceptChangeIdentityEmail["client"]["parameters"];
} = {}): UseQueryResult<TData, AcceptChangeIdentityEmail["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? acceptChangeIdentityEmailQueryKey(requestedEmailChangeToken);
    const query = useQuery({
        ...acceptChangeIdentityEmailQueryOptions(requestedEmailChangeToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, AcceptChangeIdentityEmail["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const acceptChangeIdentityEmailSuspenseQueryKey = (requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"]) => [{ url: "/api/identity/change-email/:requestedEmailChangeToken", params: { requestedEmailChangeToken: requestedEmailChangeToken } }] as const;
export type AcceptChangeIdentityEmailSuspenseQueryKey = ReturnType<typeof acceptChangeIdentityEmailSuspenseQueryKey>;
export function acceptChangeIdentityEmailSuspenseQueryOptions(requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"], options: AcceptChangeIdentityEmail["client"]["parameters"] = {}) {
    const queryKey = acceptChangeIdentityEmailSuspenseQueryKey(requestedEmailChangeToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<AcceptChangeIdentityEmail["data"], AcceptChangeIdentityEmail["error"]>({
                method: "get",
                url: `/api/identity/change-email/${requestedEmailChangeToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Change email confirmation
 * @link /api/identity/change-email/:requestedEmailChangeToken
 */
export function useAcceptChangeIdentityEmailSuspense<TData = AcceptChangeIdentityEmail["response"], TQueryKey extends QueryKey = AcceptChangeIdentityEmailSuspenseQueryKey>(requestedEmailChangeToken: AcceptChangeIdentityEmailPathParams["requestedEmailChangeToken"], options: {
    query?: Partial<UseSuspenseQueryOptions<AcceptChangeIdentityEmail["response"], AcceptChangeIdentityEmail["error"], TData, TQueryKey>>;
    client?: AcceptChangeIdentityEmail["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, AcceptChangeIdentityEmail["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? acceptChangeIdentityEmailSuspenseQueryKey(requestedEmailChangeToken);
    const query = useSuspenseQuery({
        ...acceptChangeIdentityEmailSuspenseQueryOptions(requestedEmailChangeToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, AcceptChangeIdentityEmail["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}